import 'leaflet/dist/leaflet.css'

import {
  divIcon,
  layerGroup,
  map as createMap,
  marker as createMarker,
  polyline,
  tileLayer,
  type DivIcon,
  type LatLngExpression,
  type LayerGroup,
  type Map as LeafletMap,
  type Marker,
} from 'leaflet'
import { useEffect, useRef, type ReactNode } from 'react'

import type { GeoPoint } from '@aeris/contracts'
import { cn } from '@aeris/ui'

import { MAP_ATTRIBUTION, MAP_TILE_URL } from '../lib/map-tiles.ts'

/**
 * O mapa das entregas (F265–F267): Leaflet com os tiles do OpenStreetMap.
 *
 * O Google entra só no servidor (achar o endereço, os tempos, o caminho): a
 * chave dele nunca vem para o navegador nem para o desktop. Aqui é só
 * desenho — o pino que a pessoa confere, os pontos das paradas e o caminho.
 *
 * O pino arrastável também anda pelo teclado: com ele focado, as setas movem
 * uns 5 m (com Shift, uns 50 m). Quem não usa mouse confere o ponto do mesmo
 * jeito, e o endereço por extenso está sempre ao lado do mapa.
 */

export type MapMarker = {
  readonly id: string
  readonly point: GeoPoint
  /** O texto do marcador: o número da parada, "S" da saída. */
  readonly label: string
  /** A cor do entregador (F266). */
  readonly color?: string
  readonly title?: string
}

export type MapPath = {
  readonly id: string
  readonly points: readonly GeoPoint[]
  readonly color: string
}

export type MapPin = {
  readonly point: GeoPoint
  readonly onMove: (point: GeoPoint) => void
}

/** ~5 m em graus de latitude. */
const NUDGE = 0.000045

const labelIcon = (label: string, color: string): DivIcon =>
  divIcon({
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `<span style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:${color};color:#fff;font:600 12px/1 system-ui,sans-serif;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">${label
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')}</span>`,
  })

const PIN_ICON = divIcon({
  className: '',
  iconSize: [30, 42],
  iconAnchor: [15, 41],
  html: '<svg width="30" height="42" viewBox="0 0 30 42" aria-hidden="true"><path d="M15 1C7.3 1 1 7.2 1 14.9 1 25.3 15 41 15 41s14-15.7 14-26.1C29 7.2 22.7 1 15 1z" fill="#dc2626" stroke="#fff" stroke-width="2"/><circle cx="15" cy="15" r="5" fill="#fff"/></svg>',
})

const toLatLng = (point: GeoPoint): LatLngExpression => [
  point.latitude,
  point.longitude,
]

/** Vazios estáveis: um `[]` novo a cada render redesenharia o mapa toda vez. */
const NO_MARKERS: readonly MapMarker[] = []
const NO_PATHS: readonly MapPath[] = []

export const MapView = ({
  center,
  zoom = 16,
  pin,
  markers = NO_MARKERS,
  paths = NO_PATHS,
  className,
  label,
}: {
  center: GeoPoint
  zoom?: number
  pin?: MapPin | null
  markers?: readonly MapMarker[]
  paths?: readonly MapPath[]
  className?: string
  /** O nome acessível do mapa. */
  label: string
}): ReactNode => {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<LeafletMap | null>(null)
  const layers = useRef<LayerGroup | null>(null)
  const pinMarker = useRef<Marker | null>(null)
  const onMove = useRef<MapPin['onMove'] | null>(null)
  const initial = useRef({ center, zoom })

  const centerLat = center.latitude
  const centerLng = center.longitude
  const pinLat = pin?.point.latitude ?? null
  const pinLng = pin?.point.longitude ?? null
  const pinMove = pin?.onMove ?? null

  // Quem arrasta chama a função mais nova, sem recriar o pino.
  useEffect(() => {
    onMove.current = pinMove
  }, [pinMove])

  // O mapa nasce uma vez por montagem.
  useEffect(() => {
    if (container.current === null) return
    const created = createMap(container.current, {
      center: toLatLng(initial.current.center),
      zoom: initial.current.zoom,
      zoomControl: true,
    })
    tileLayer(MAP_TILE_URL, {
      attribution: MAP_ATTRIBUTION,
      maxZoom: 19,
    }).addTo(created)
    layers.current = layerGroup().addTo(created)
    map.current = created
    return () => {
      created.remove()
      map.current = null
      layers.current = null
      pinMarker.current = null
    }
  }, [])

  useEffect(() => {
    const current = map.current
    current?.setView([centerLat, centerLng], current.getZoom())
  }, [centerLat, centerLng])

  // Os marcadores e os caminhos.
  useEffect(() => {
    const group = layers.current
    if (group === null) return
    group.clearLayers()
    for (const path of paths) {
      if (path.points.length < 2) continue
      polyline(path.points.map(toLatLng), {
        color: path.color,
        weight: 4,
        opacity: 0.8,
      }).addTo(group)
    }
    for (const entry of markers) {
      createMarker(toLatLng(entry.point), {
        icon: labelIcon(entry.label, entry.color ?? '#2563eb'),
        title: entry.title ?? entry.label,
        alt: entry.title ?? entry.label,
        keyboard: true,
      }).addTo(group)
    }
  }, [markers, paths])

  // O pino que a pessoa confere.
  useEffect(() => {
    const created = map.current
    if (created === null) return
    if (pinLat === null || pinLng === null) {
      pinMarker.current?.remove()
      pinMarker.current = null
      return
    }
    const existing = pinMarker.current
    if (existing !== null) {
      existing.setLatLng([pinLat, pinLng])
      return
    }
    const placed = createMarker([pinLat, pinLng], {
      icon: PIN_ICON,
      draggable: true,
      keyboard: true,
      title: 'O ponto da entrega. Arraste, ou use as setas.',
      alt: 'O ponto da entrega',
      autoPan: true,
    }).addTo(created)
    placed.on('dragend', () => {
      const at = placed.getLatLng()
      onMove.current?.({ latitude: at.lat, longitude: at.lng })
    })
    placed.getElement()?.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? NUDGE * 10 : NUDGE
      const at = placed.getLatLng()
      const moves: Record<string, [number, number]> = {
        ArrowUp: [step, 0],
        ArrowDown: [-step, 0],
        ArrowLeft: [0, -step],
        ArrowRight: [0, step],
      }
      const move = moves[event.key]
      if (move === undefined) return
      event.preventDefault()
      const next = { latitude: at.lat + move[0], longitude: at.lng + move[1] }
      placed.setLatLng(toLatLng(next))
      onMove.current?.(next)
    })
    pinMarker.current = placed
  }, [pinLat, pinLng])

  return (
    <div
      ref={container}
      role="region"
      aria-label={label}
      className={cn(
        'h-72 w-full overflow-hidden rounded-(--radius-md) border border-(--border-subtle)',
        className
      )}
    />
  )
}
